import type { Either } from 'fp-ts/Either';
import { fold, isLeft, mapLeft, right } from 'fp-ts/Either';
import { pipe } from 'fp-ts/pipeable';
/* eslint-disable-next-line no-restricted-imports */
import type * as T from 'io-ts';
/* eslint-disable-next-line no-restricted-imports */
import { PathReporter } from 'io-ts/PathReporter';
import type { z } from 'zod';

import { ValidationError } from '@jane/business-admin/types';

function isZodSchema(
  type: T.Type<any, any, any> | z.ZodTypeAny
): type is z.ZodTypeAny {
  return (type as z.ZodTypeAny).safeParse !== undefined;
}

const decodeWith = <
  ApplicationType = any,
  EncodeTo = ApplicationType,
  DecodeFrom = unknown,
>(
  type: T.Type<ApplicationType, EncodeTo, DecodeFrom> | z.ZodTypeAny,
  input: DecodeFrom,
  url: string
) => {
  let validation: Either<any, ApplicationType>;

  if (isZodSchema(type)) {
    const result = type.safeParse(input);

    if (result.success) {
      validation = right(result.data as ApplicationType);
    } else {
      const error = new Error(
        [
          JSON.stringify(result.error.issues, null, 2),
          '█',
          `${url}`,
          '█',
          'Received:',
          JSON.stringify(input, null, 2),
        ].join('\n')
      );

      console.error(error);
      validation = right(input as any);
    }
  } else {
    let validation = type.decode(input);

    // Copied from: libs/monolith/shared/src/lib/lib/loadable.ts
    if (isLeft(validation)) {
      const error = new Error(
        [
          PathReporter.report(validation),
          '█',
          `${url}`,
          '█',
          'Received:',
          JSON.stringify(input, null, 2),
        ].join('\n')
      );

      validation = right(input as any);
      console.error(error);
    }

    return pipe(
      validation,
      mapLeft((errors) => new ValidationError(errors, input))
    );
  }

  return pipe(
    validation,
    mapLeft((errors) => new ValidationError(errors, input))
  );
};

const throwValidationErrorOrReturnValue = <T>(
  value: Either<ValidationError, T>
) =>
  fold<ValidationError, T, T>(
    (error) => {
      throw error;
    },
    (response) => response
  )(value);

export const fetchWithDecode = async (
  request: Promise<any>,
  type: T.Type<any> | z.ZodTypeAny,
  url: string
) => {
  const response = await request;

  if (process.env['NODE_ENV'] === 'production') {
    return response;
  }

  const data = decodeWith(type, response, url);

  return throwValidationErrorOrReturnValue(data);
};
